/**
 * Thesis Fit — Zustand Store
 *
 * Manages the investor's active thesis profile with AsyncStorage persistence.
 * Follows the domain-split pattern (separate from profile-store, score-store, etc.).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';

import type { ScoringDimension } from '@/lib/scoring/methodology';
import type { ThesisProfile, ThesisArchetype } from './types';
import { getArchetypeWeights } from './archetypes';
import { redistributeWeights } from './compute';

// =============================================================================
// State Interface
// =============================================================================

interface ThesisState {
  activeThesis: ThesisProfile | null;

  // Actions
  setThesis: (thesis: ThesisProfile) => void;
  createFromArchetype: (investorId: string, archetype: ThesisArchetype) => void;
  updateWeight: (dimension: ScoringDimension, value: number) => void;
  setArchetype: (archetype: ThesisArchetype) => void;
  clearThesis: () => void;
  reset: () => void;
}

const initialState = {
  activeThesis: null as ThesisProfile | null,
};

// =============================================================================
// Store
// =============================================================================

export const useThesisStore = create<ThesisState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setThesis: (thesis) => set({ activeThesis: thesis }),

      createFromArchetype: (investorId, archetype) => {
        const now = new Date().toISOString();
        const weights = getArchetypeWeights(archetype);
        const thesis: ThesisProfile = {
          id: `local-${Date.now()}`,
          investorId,
          name: archetype === 'custom' ? 'My Thesis' : archetype,
          archetype,
          weights,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        };
        set({ activeThesis: thesis });
      },

      updateWeight: (dimension, value) => {
        const current = get().activeThesis;
        if (!current) return;

        const newWeights = redistributeWeights(current.weights, dimension, value);
        set({
          activeThesis: {
            ...current,
            weights: newWeights,
            archetype: 'custom', // Editing weights switches to custom
            updatedAt: new Date().toISOString(),
          },
        });
      },

      setArchetype: (archetype) => {
        const current = get().activeThesis;
        if (!current) return;

        const weights = getArchetypeWeights(archetype);
        set({
          activeThesis: {
            ...current,
            archetype,
            weights,
            name: archetype === 'custom' ? current.name : archetype,
            updatedAt: new Date().toISOString(),
          },
        });
      },

      clearThesis: () => set({ activeThesis: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'inverio-thesis',
      version: 1,
      storage: createJSONStorage(getStorage),
      partialize: (state) => ({
        activeThesis: state.activeThesis,
      }),
    },
  ),
);
